import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Container,
  Table,
  Alert,
  Spinner,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { getLeasesByProperty } from '../../utils';
import { setLeaseStatuses } from '../../../../../../../../../../store/leaseStatuses/actions';
import { archiveMastersheetItem } from '../../../../../../../TenantForm/Helpers/api';

const LeaseStatus = {
  ON_HOLD: 'On Hold',
  CANCELLED: 'Cancelled',
  WAITING_FOR_DOCUMENTS: 'Waiting on Documents',
  ACTIVE_NEW: 'Active - New Lease',
  ACTIVE_RENEWAL: 'Active - Renewal Lease',
  AMENDMENT: 'Amendment',
};

const statusColors = {
  'On Hold': { text: '#856404', bg: '#fff3cd', border: '#ffeaa7' },
  'Cancelled': { text: '#721c24', bg: '#f8d7da', border: '#f5c6cb' },
  'Waiting on Documents': { text: '#383d7c', bg: '#e2e3f3', border: '#c3c6ef' },
  'Active - New Lease': { text: '#155724', bg: '#d4edda', border: '#c3e6cb' },
  'Active - Renewal Lease': { text: '#0d4b6e', bg: '#d1ecf1', border: '#bee5eb' },
  'Amendment': { text: '#2e6b47', bg: '#e6f4ea', border: '#c8e6c9' },
};

const SetLeaseAsActive = () => {
  const { property_id, building_name, unit_no } = useParams();
  const [leases, setLeases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const leaseStatuses = useSelector((state) => state.leaseStatus?.leaseStatuses || {});

  useEffect(() => {
    const fetchLeases = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getLeasesByProperty(property_id, building_name, unit_no);
        if (response.success) {
          setLeases(response.data || []);
          const initialStatuses = (response.data || []).reduce((acc, lease) => ({
            ...acc,
            [lease._id]: lease.lease_status?.state || 'Cancelled',
          }), {});
          dispatch(setLeaseStatuses(initialStatuses));
        } else {
          throw new Error(response.message || 'Failed to fetch leases');
        }
      } catch (err) {
        setError(err.message || 'Error fetching leases');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to fetch leases',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (property_id && building_name && unit_no) {
      fetchLeases();
    } else {
      setError('Missing required parameters');
      setIsLoading(false);
    }
  }, [property_id, building_name, unit_no, dispatch]);

  const proceedWithStatusUpdate = (leaseId, status) => {
    const updatedStatuses = {};
    Object.keys(leaseStatuses).forEach((id) => {
      // For AMENDMENT, do not change other leases' statuses
      if (status === 'Amendment') {
        updatedStatuses[id] = leaseStatuses[id];
      } else {
        updatedStatuses[id] = id === leaseId && 
          (status === 'Active - New Lease' || status === 'Active - Renewal Lease') 
          ? status 
          : 'Cancelled';
      }
    });
    if (status !== 'Active - New Lease' && status !== 'Active - Renewal Lease') {
      updatedStatuses[leaseId] = status;
    }
    dispatch(setLeaseStatuses(updatedStatuses));
  };

  const handleStatusUpdate = async (leaseId, newStatus) => {
    if (!leaseId || !newStatus) return;

    const currentStatus = leaseStatuses[leaseId];

    if (newStatus === 'Active - New Lease' && currentStatus !== 'Active - New Lease') {
      const { value: archivedReason, isConfirmed } = await Swal.fire({
        title: 'Archive and Activate New Lease',
        text: 'Please enter a reason to archive the current tenant information before activating the new lease.',
        icon: 'warning',
        input: 'text',
        inputPlaceholder: 'Reason for archiving',
        showCancelButton: true,
        confirmButtonText: 'Archive and Activate',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        inputValidator: (value) => !value && 'Reason is required',
      });

      if (!isConfirmed || !archivedReason) return;

      try {
        Swal.fire({ title: 'Archiving...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        const lease = leases.find((l) => l._id === leaseId);
        const { success, data } = await archiveMastersheetItem(
          lease?.mastersheet_id,
          property_id,
          building_name,
          unit_no,
          archivedReason
        );

        if (!success) throw new Error(data?.message || 'Archival failed');

        Swal.close();
        proceedWithStatusUpdate(leaseId, 'Active - New Lease');
      } catch (error) {
        console.error('Error archiving unit:', error);
        Swal.fire('Error', error.message || 'Failed to archive.', 'error');
      }
      return;
    }

    if (newStatus === 'Active - Renewal Lease' && currentStatus !== 'Active - Renewal Lease') {
      try {
        Swal.fire({ title: 'Updating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        proceedWithStatusUpdate(leaseId, 'Active - Renewal Lease');
        Swal.close();
      } catch (error) {
        console.error('Error updating renewal lease:', error);
        Swal.fire('Error', error.message || 'Failed to update renewal lease.', 'error');
      }
      return;
    }

    if (newStatus === 'Amendment' && currentStatus !== 'Amendment') {
      try {
        Swal.fire({ title: 'Updating to Amendment...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        proceedWithStatusUpdate(leaseId, 'Amendment');
        Swal.close();
      } catch (error) {
        console.error('Error updating to amendment:', error);
        Swal.fire('Error', error.message || 'Failed to update to amendment.', 'error');
      }
      return;
    }

    proceedWithStatusUpdate(leaseId, newStatus);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : 'Invalid Date';
  };

  const getCurrentStatus = (lease) => {
    return leaseStatuses[lease._id] || lease.lease_status?.state || 'Cancelled';
  };

  const getStatusDisplayName = (statusKey) => {
    return LeaseStatus[statusKey] || statusKey;
  };

  if (isLoading) {
    return (
      <Container className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading leases...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 d-flex justify-content-center">
        <Alert color="danger" className="text-center w-50">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          .status-select option {
            padding: 8px 12px !important;
            font-weight: 600 !important;
          }
          
          .status-select option[value="Active - New Lease"] {
            color: #155724 !important;
            background-color: #d4edda !important;
          }
          
          .status-select option[value="Active - Renewal Lease"] {
            color: #0d4b6e !important;
            background-color: #d1ecf1 !important;
          }
          
          .status-select option[value="Cancelled"] {
            color: #721c24 !important;
            background-color: #f8d7da !important;
          }
          
          .status-select option[value="On Hold"] {
            color: #856404 !important;
            background-color: #fff3cd !important;
          }
          
          .status-select option[value="Waiting on Documents"] {
            color: #383d7c !important;
            background-color: #e2e3f3 !important;
          }
          
          .status-select option[value="Amendment"] {
            color: #2e6b47 !important;
            background-color: #e6f4ea !important;
          }
        `}
      </style>
      
      <Card className="shadow-lg p-3 lease-form" style={{ backgroundColor: 'white', minHeight: '50vh' }}>
        {leases.length === 0 ? (
          <Alert color="info" className="text-center">No leases found for this property, building, and unit.</Alert>
        ) : (
          <Table striped hover responsive className="equal-width-table text-center">
            <thead>
              <tr>
                <th>Lease ID</th>
                <th>Tenant(s)</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Current Status</th>
                <th>Update Status</th>
              </tr>
            </thead>
            <tbody>
              {leases.map((lease) => {
                const currentStatus = getCurrentStatus(lease);
                const currentColors = statusColors[currentStatus] || { text: '#000', bg: '#fff', border: '#ced4da' };
                
                return (
                  <tr key={lease._id}>
                    <td className="centered-cell">{lease._id}</td>
                    <td className="centered-cell">{lease.tenants?.map((t) => t.name).join(', ') || 'N/A'}</td>
                    <td className="centered-cell">
                      {formatDateForDisplay(lease.lease_details?.start_date)}
                    </td>
                    <td className="centered-cell">
                      {formatDateForDisplay(lease.lease_details?.end_date)}
                    </td>
                    <td
                      className="centered-cell"
                      style={{
                        fontWeight: 600,
                        color: currentColors.text,
                        backgroundColor: currentColors.bg,
                        borderRadius: '4px',
                        padding: '6px 12px',
                      }}
                    >
                      {getStatusDisplayName(currentStatus)}
                    </td>
                    <td className="centered-cell align-middle" style={{ padding: '8px' }}>
                      <FormGroup className="mb-0">
                        <Label className="visually-hidden">Status</Label>
                        <Input
                          type="select"
                          className="status-select"
                          value={currentStatus}
                          onChange={(e) => handleStatusUpdate(lease._id, e.target.value)}
                          style={{
                            minHeight: '32px',
                            fontSize: '0.875rem',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            color: currentColors.text,
                            backgroundColor: currentColors.bg,
                            fontWeight: '600',
                            border: `1px solid ${currentColors.border}`,
                            cursor: 'pointer',
                          }}
                        >
                          {Object.entries(LeaseStatus).map(([key, label]) => {
                            const optionColors = statusColors[label] || { text: '#000', bg: '#fff' };
                            return (
                              <option 
                                key={key} 
                                value={label}
                                style={{ 
                                  color: optionColors.text,
                                  backgroundColor: optionColors.bg,
                                  fontWeight: '600',
                                  padding: '8px 12px'
                                }}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
};

export { SetLeaseAsActive };